import type { AdditionalDetailsShiftProps } from '@tempmee/core';
import type { ReceiptProps } from '@tempmee/react-blocks';
import { UserProfession } from '~/enums/UserProfession';
import { formatFromMinuteToFormattedHour } from '~/utils/DateUtils';
import { isEmployeeOptionShift } from '~/components/WorkerClassification/utils';
import { CONVENIENCE_FEE_INFO, OVERTIME_INFO } from '~/constants/billing';
import { PROMO_ROW_LABEL } from '~/constants/promo';
import { IMultipleCounters } from '~/models';
import { EducationUtils } from '~/utils/EducationUtils';
import { formatMoney, formatMoneyCredit } from '~/utils/formatMoney';
import { getProfessionalName } from '~/utils/professional';
import { type TShiftLike } from './shiftSummaryProps';

/**
 * Renders a fee ratio as the parenthetical percentage, or `''` when there is none.
 *
 * Pass `null` as the ratio to format a percentage the shift already carries
 * directly (`tm_fee_percent`), which is how the cancellation receipt reads them.
 */
export const feePercent = (ratio?: number | null, fallbackPercent?: number | null): string => {
  const percent = ratio != null ? ratio * 100 : fallbackPercent;

  if (percent == null || percent === 0) {
    return '';
  }

  const rounded = Number(percent).toFixed(1);

  return ` (${rounded}%)`;
};

/** Rendered where a shift attribute does not apply, as the Figma draws it. */
export const EM_DASH = '\u2014';

/**
 * The nine `AdditionalDetails` row values, as the block's named-prop contract
 * takes them. `variant` and the note stay with the page: the note is editable on
 * `open-confirm` and read-only afterwards, and only the page knows which.
 *
 * Every row is supplied, never dropped. The block's rows are fixed by the Figma
 * (`5817:18351`), so an attribute that does not apply is an em dash rather than a
 * missing row — dropping one reflows the two-column grid and the panel would
 * change shape from shift to shift.
 */
export type TAdditionalDetailsRows = Pick<
  AdditionalDetailsShiftProps,
  | 'location'
  | 'speciality'
  | 'meal'
  | 'isMealPaid'
  | 'timePerPatient'
  | 'assistedHygienist'
  | 'autoConfirm'
  | 'isAutoConfirmOn'
  | 'postedBy'
  | 'performedBy'
>;

/**
 * The office the shift is at, by name.
 *
 * The street address is deliberately left out: the panel names the office and
 * the full address made the row wrap on a narrow panel (design, 2026-09-15).
 */
const resolveLocation = (job: TShiftLike): string => job?.offer_owner?.office_name || EM_DASH;

/**
 * Which auto-confirm pools are on. The legacy page prints one line per pool with
 * its own On/Off; the block's row is a single value with one status chip, so the
 * enabled pools are named and the chip carries the on/off state.
 *
 * With auto-confirm off there is nothing to name, so the value is an em dash
 * like every other empty row rather than the sentence "No professionals"
 * (design, 2026-09-15).
 */
const resolveAutoConfirm = (job: TShiftLike): { value: string; isOn: boolean } => {
  const pools = [
    job?.autofillOptions?.favorite ? 'Favorites' : undefined,
    job?.autoFill ? 'Eligible Professionals' : undefined,
  ].filter(Boolean);

  return pools.length > 0
    ? { value: pools.join(', '), isOn: true }
    : { value: EM_DASH, isOn: false };
};

/**
 * Assisted hygiene is a hygienist-only arrangement, so the value only means
 * something on an RDH shift — the same gate the legacy details applied. On any
 * other profession the row stays, showing an em dash.
 */
const resolveAssistedHygienist = (job: TShiftLike): string => {
  if (UserProfession.getNormalizedCode(job?.profession ?? '') !== UserProfession.RDH) {
    return EM_DASH;
  }

  return job?.isAssistedHygiene ? 'Yes' : 'No';
};

/**
 * Maps a shift onto the panel's rows.
 *
 * `performedBy` is passed through **untouched**, and that is load-bearing: the
 * block draws no row at all when it is `undefined`, which is how an open shift
 * avoids claiming "Confirmed by —" for something that can never be confirmed.
 * Defaulting it to an em dash here would put the row back on every page and
 * quietly undo that.
 *
 * So the page decides, because only the page knows its own state:
 *
 * - open / confirm-candidates — omit it; nobody has confirmed the shift yet
 * - scheduled / completed — `name || EM_DASH`; somebody did confirm it, and a
 *   name we cannot read is an unfilled value rather than an absent row
 * - cancelled — same, except an **expired** shift omits it: nobody cancelled it
 *
 * `postedBy` names whoever posted the shift, read from `author` (GOTU-8131) the
 * same way the pages read `confirmPerformedBy`. Every shift was posted by
 * someone, so a poster the backend could not name (`author: null`, or an older
 * payload without the key) is an em dash rather than a missing row.
 *
 * There is deliberately no same-account check: an office that posted from its
 * own login is still named (product, 2026-10-16). A DSO parent posting into a
 * location is named by the account holder, which is what `author` already
 * holds, and admin posters arrive masked as "GoTu Admin" by the backend.
 */
export const toAdditionalDetailsRows = (
  job: TShiftLike,
  performedBy?: string | null,
): TAdditionalDetailsRows => {
  const mealMinutes = Number(job?.lunch_final_break_time ?? job?.lunch_break_time);
  const autoConfirm = resolveAutoConfirm(job);

  return {
    location: resolveLocation(job),
    speciality: EducationUtils.removeSpecialtyPrefix(job?.specialty) || 'General',
    meal: mealMinutes > 0 ? formatFromMinuteToFormattedHour(mealMinutes) : EM_DASH,
    isMealPaid: Boolean(job?.is_lunch_break_paid),
    timePerPatient: job?.patientsPerHour || EM_DASH,
    assistedHygienist: resolveAssistedHygienist(job),
    autoConfirm: autoConfirm.value,
    isAutoConfirmOn: autoConfirm.isOn,
    postedBy: getProfessionalName(job?.author) || EM_DASH,
    performedBy: performedBy ?? undefined,
  };
};

/** The `Receipt` block's data props, assembled from a shift and (when one is picked) a counter offer. */
export interface IReceiptView {
  lines: ReceiptProps['lines'];
  total: string;
}

/**
 * Shift (+ the selected candidate's counter offer) → the Receipt panel.
 *
 * Amounts come from the counter offer's `payment` when a candidate is selected
 * and fall back to the shift's own totals otherwise: on the multi-candidate list
 * there is no counter to read, and showing the first applicant's arithmetic for
 * the whole shift would be a quiet lie. The fallbacks are the same `/jobs/:id`
 * fields the legacy screen used before a candidate was chosen.
 *
 * `total` is the backend's number, never a local sum — the office is charged what
 * the server computed, and re-adding the lines here would let the two drift.
 */
export const toReceipt = ({
  job,
  counter,
}: {
  job: TShiftLike;
  counter?: IMultipleCounters | null;
}): IReceiptView => {
  const payment = counter?.payment;

  const serviceFee = payment?.tempmeeFee ?? job?.tm_fee;
  const convenienceFee = payment?.convenienceFee ?? job?.convenience_fee;
  const employeeFee = payment?.peoFee ?? job?.employeeOptionFee;
  const promoAmount = payment?.promoDiscount?.amount ?? job?.promoDiscount?.amount;

  const regularHours = job?.regularAndOvertimeHours?.regular ?? 0;
  const overtimeHours = job?.regularAndOvertimeHours?.overtime ?? 0;

  const lines: ReceiptProps['lines'] = [];

  if (regularHours > 0 && (job?.totalRegular ?? 0) > 0) {
    const hours = regularHours.toFixed(2);

    lines.push({ label: `Regular Rate Pay (${hours} hrs)`, value: formatMoney(job?.totalRegular) });
  }

  if (overtimeHours > 0 && (job?.totalOvertime ?? 0) > 0) {
    const hours = overtimeHours.toFixed(2);

    lines.push({
      label: `Overtime Pay (${hours} hrs)`,
      value: formatMoney(job?.totalOvertime),
      info: OVERTIME_INFO,
    });
  }

  lines.push({
    label: `Service Fee${feePercent(payment?.tempmeeFeeRatio, job?.tm_fee_percent)}`,
    value: formatMoney(serviceFee),
  });

  if (employeeFee && isEmployeeOptionShift(job)) {
    lines.push({ label: 'Employee Fee', value: formatMoney(employeeFee) });
  }

  if (convenienceFee) {
    const percent = feePercent(payment?.convenienceFeeRatio, job?.convenience_fee_percent);

    lines.push({
      label: `Convenience Fee${percent}`,
      value: formatMoney(convenienceFee),
      info: CONVENIENCE_FEE_INFO,
    });
  }

  if (job?.counterCover?.amount) {
    lines.push({ label: 'GoTu Counter Cover', value: formatMoneyCredit(job.counterCover.amount) });
  }

  if (promoAmount) {
    lines.push({ label: PROMO_ROW_LABEL, value: formatMoneyCredit(promoAmount) });
  }

  return { lines, total: formatMoney(payment?.totalAmount ?? job?.total) };
};
